import { useRef } from "react";
import { X, Download, FileText } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useModalA11y } from "@/hooks/use-modal-a11y";
import {
  LEGAL_DOCUMENTS,
  resolveDocLang,
  type DocId,
} from "./legal-documents";

const UI: Record<"de" | "en", { close: string; download: string; reading: string }> = {
  en: { close: "Close", download: "Download PDF", reading: "Reading view" },
  de: { close: "Schließen", download: "PDF herunterladen", reading: "Leseansicht" },
};

export function LegalDocModal({ docId, onClose }: { docId: DocId; onClose: () => void }) {
  const { lang } = useI18n();
  const docLang = resolveDocLang(lang);
  const ui = UI[docLang];
  const doc = LEGAL_DOCUMENTS[docId][docLang];
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useModalA11y({
    open: true,
    onClose,
    analyticsId: `legal_doc_${docId}`,
    initialFocusRef: closeRef,
  });

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-obsidian/80 px-3 py-6 backdrop-blur-md sm:px-6 sm:py-10"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={doc.title}
        tabIndex={-1}
        className="relative w-full max-w-3xl rounded-3xl glass luminous-border p-5 shadow-[var(--shadow-elev)] outline-none sm:p-8"
      >
        <div className="flex items-start gap-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-saffron/20 to-coral/20 text-saffron">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-saffron">
              {ui.reading} · {docLang.toUpperCase()} · {doc.version}
            </span>
            <h2 className="mt-1 font-display text-xl font-medium leading-tight tracking-tight sm:text-2xl">
              {doc.title}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{doc.subtitle}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {doc.updated}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={ui.close}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 max-h-[62vh] space-y-6 overflow-y-auto pr-1">
          {doc.sections.map((s) => (
            <section key={s.heading}>
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-saffron">
                {s.heading}
              </h3>
              {s.body?.map((p) => (
                <p key={p} className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
              {s.facts && (
                <dl className="mt-3 grid gap-2 sm:grid-cols-2">
                  {s.facts.map((f) => (
                    <div
                      key={f.label}
                      className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2"
                    >
                      <dt className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {f.label}
                      </dt>
                      <dd className="mt-0.5 font-mono text-sm text-foreground">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {s.bullets && (
                <ul className="mt-3 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex gap-2 text-sm leading-relaxed text-muted-foreground"
                    >
                      <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-saffron" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <a
          href={doc.pdf}
          download
          rel="noopener"
          className="liquid-btn mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-saffron to-coral px-6 py-3 text-sm font-bold tracking-wide text-obsidian"
        >
          <Download className="h-4 w-4" />
          {ui.download}
        </a>
      </div>
    </div>
  );
}
